import { useState } from 'react';
import { Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { quotePdfDownloadPath, quotePdfFileNameSchema } from './quotePdfContract';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';

export function QuotePdfDownload({
  href,
  fileName,
}: {
  href: string;
  fileName?: string | undefined;
}) {
  const { t } = useTranslation('assistant');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const path = quotePdfDownloadPath(href);
  const download = async (): Promise<void> => {
    if (!path || busy) return;
    setBusy(true);
    setFailed(false);
    let objectUrl: string | undefined;
    const sessionEpoch = useAuthStore.getState().sessionEpoch;
    try {
      const blob = await api.getBlob(path);
      const currentSession = useAuthStore.getState();
      if (currentSession.sessionEpoch !== sessionEpoch || currentSession.isLocked)
        throw new Error('Session changed during download');
      if (blob.type !== 'application/pdf' || blob.size === 0)
        throw new Error('Invalid PDF response');
      objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = quotePdfFileNameSchema.safeParse(fileName).success
        ? fileName!
        : 'Tarjous.pdf';
      document.body.appendChild(link);
      try {
        link.click();
      } finally {
        link.remove();
      }
    } catch {
      setFailed(true);
    } finally {
      if (objectUrl) {
        const createdUrl = objectUrl;
        window.setTimeout(() => URL.revokeObjectURL(createdUrl), 60_000);
      }
      setBusy(false);
    }
  };
  return (
    <>
      <Link
        component="button"
        type="button"
        disabled={busy || !path}
        onClick={() => void download()}
        underline="hover"
      >
        {t(busy ? 'quotePdf.downloading' : 'quotePdf.download')}
      </Link>
      {failed && (
        <Typography
          component="span"
          variant="caption"
          color="error"
          role="alert"
          sx={{ display: 'block' }}
        >
          {t('quotePdf.failed')}
        </Typography>
      )}
    </>
  );
}
